import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { EmployeeHeader } from "../components/EmployeeHeader";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ImageViewer } from "../components/vc/ImageViewer";
import { ChatButton } from "../components/vc/ChatButton";
import {
  ArrowLeft, Phone, ShoppingBag, User as UserIcon, Truck, Calendar, Camera, Save,
  CheckCircle2, Loader2, Clock, AlertTriangle, X, Circle, ListChecks, MessageSquarePlus,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BTN = {
  todo: { tone: "bg-slate-700 hover:bg-slate-800", label: "To do", Icon: Clock },
  in_progress: { tone: "bg-amber-500 hover:bg-amber-600", label: "In Progress", Icon: Loader2 },
  completed: { tone: "bg-emerald-600 hover:bg-emerald-700", label: "Completed", Icon: CheckCircle2 },
};

const PRIORITY_TONE = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-sky-100 text-sky-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

async function compressImage(file, maxWidth = 1024, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function isOverdue(dl, status) {
  if (!dl || status === "completed") return false;
  return dl < new Date().toISOString().slice(0, 10);
}

export default function EmployeeTaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState("");
  const [taskNote, setTaskNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [togglingIdx, setTogglingIdx] = useState(null);
  const fileRef = useRef();

  const refresh = async () => {
    try {
      const { data } = await apiClient.get(`/me/tasks/${id}`);
      setData(data);
      setNote(data.task.completion_note || "");
      setPhoto(data.task.completion_photo || "");
      setTaskNote(data.task.employee_note || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
      navigate("/my-tasks");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [id]);

  const updateStatus = async (status) => {
    setUpdating(true);
    try {
      const payload = { status };
      if (status === "completed") {
        payload.completion_note = note;
        payload.completion_photo = photo;
      }
      const { data: updated } = await apiClient.put(`/me/tasks/${id}`, payload);
      setData((d) => ({ ...d, task: updated }));
      toast.success(`Status: ${status.replace("_", " ")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setUpdating(false); }
  };

  const toggleChecklistItem = async (idx, completed) => {
    setTogglingIdx(idx);
    try {
      const { data: updated } = await apiClient.put(`/me/tasks/${id}/checklist`, {
        item_index: idx,
        completed,
      });
      setData((d) => ({ ...d, task: updated }));
      if (updated.status === "completed") {
        toast.success("All items done — task completed!");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setTogglingIdx(null); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      // Hard cap ~ 600KB to keep DB lean
      if (dataUrl.length > 700000) {
        toast.error("Photo too large after compression. Try a smaller image.");
        return;
      }
      setPhoto(dataUrl);
    } catch {
      toast.error("Could not read photo");
    } finally {
      e.target.value = "";
    }
  };

  const saveTaskNote = async () => {
    setSavingNote(true);
    try {
      const { data: updated } = await apiClient.put(`/me/tasks/${id}/note`, { note: taskNote });
      setData((d) => ({ ...d, task: updated }));
      toast.success("Note sent to your boss");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSavingNote(false); }
  };

  if (loading) {
    return (
      <>
        <EmployeeHeader title="Task" />
        <div className="px-4 pt-4 text-center text-slate-500">Loading...</div>
      </>
    );
  }
  if (!data) return null;

  const t = data.task;
  const overdue = isOverdue(t.deadline, t.status);
  const isCancelled = t.status === "cancelled";
  // Phase 4 #3/#4 — once completed, the task is permanently read-only: no
  // reopening via status buttons, no checklist edits, no new notes. Applies
  // identically whether reached from Home, Task Details, or History.
  const isCompleted = t.status === "completed";
  const isLocked = isCancelled || isCompleted;
  const totalLines = data.order && Array.isArray(data.order.lines) ? data.order.lines.length : 0;
  const checkedCount = Object.values(t.checklist || {}).filter(Boolean).length;
  const hasChecklist = totalLines > 0;
  const allItemsPrepared = !hasChecklist || checkedCount >= totalLines;

  return (
    <>
      <EmployeeHeader title="Task" />
      <div className="px-4 pt-4 pb-28 space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} data-testid="emp-task-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {data.order?.id && <ChatButton orderId={data.order.id} testid="emp-task-chat-btn" />}
        </div>

        <div className={`rounded-2xl border p-5 space-y-2 ${overdue ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-xl font-bold text-slate-900">{t.title}</h2>
            <span className={`text-[11px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
              t.status === "completed" ? "bg-emerald-100 text-emerald-700"
                : t.status === "in_progress" ? "bg-amber-100 text-amber-700"
                : t.status === "cancelled" ? "bg-red-100 text-red-700"
                : "bg-slate-100 text-slate-700"
            }`}>{t.status.replace("_", " ")}</span>
          </div>
          {t.priority && (
            <span data-testid="emp-task-priority" className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${PRIORITY_TONE[(t.priority || "").toLowerCase()] || PRIORITY_TONE.medium}`}>
              {t.priority} priority
            </span>
          )}
          {t.description && <p className="text-sm text-slate-700 whitespace-pre-wrap">{t.description}</p>}
          {t.deadline && (
            <div className={`text-sm inline-flex items-center gap-1 ${overdue ? "text-red-700 font-bold" : "text-slate-600"}`}>
              {overdue ? <AlertTriangle className="w-4 h-4" /> : <Calendar className="w-4 h-4" />} Due {t.deadline}
            </div>
          )}
        </div>

        {t.image_data && (
          <ImageViewer src={t.image_data} label="Task brief" testid="task-image-viewer" />
        )}

        {isCancelled && (
          <div data-testid="task-cancelled-banner" className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-red-800">Task cancelled by owner</div>
              <p className="text-xs text-red-700 mt-0.5">
                This task has been cancelled and can no longer be updated. Please contact your owner if this looks wrong.
              </p>
            </div>
          </div>
        )}

        {isCompleted && (
          <div data-testid="task-completed-banner" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-emerald-800">Marked as Completed</div>
              <p className="text-xs text-emerald-700 mt-0.5">
                This task is now read-only — items, statuses and notes can no longer be changed.
              </p>
            </div>
          </div>
        )}

        {(data.order || data.customer || data.supplier) && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider">Linked Order</h3>
            {data.order && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0"><ShoppingBag className="w-5 h-5" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{data.order.product}</div>
                  <div className="text-xs text-slate-500">Qty: {data.order.quantity} {data.order.unit} · ₹{Number(data.order.price || 0).toFixed(2)}</div>
                  {data.order.id && (
                    <div data-testid="task-order-reference" className="text-[11px] text-slate-400 font-mono mt-0.5">
                      Order #{(data.order.id || "").slice(0, 6).toUpperCase()}
                    </div>
                  )}
                  {(data.order.expected_delivery_date || data.order.expected_delivery_time) && (
                    <div data-testid="task-delivery-time" className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                      <Clock className="w-3 h-3" />
                      {[data.order.expected_delivery_date, data.order.expected_delivery_time].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              </div>
            )}
            {data.supplier && (
              <ContactRow icon={Truck} tone="indigo" label="Supplier" name={data.supplier.name} phone={data.supplier.phone} testid="contact-supplier" />
            )}
            {data.customer && (
              <ContactRow icon={UserIcon} tone="emerald" label="Customer" name={data.customer.name} phone={data.customer.phone} testid="contact-customer" />
            )}
          </div>
        )}

        {data.order && data.order.image_data && (
          <ImageViewer src={data.order.image_data} label="Order image" testid="order-image-viewer" />
        )}

        {data.order && Array.isArray(data.order.lines) && data.order.lines.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="task-checklist-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider flex items-center gap-1.5">
                <ListChecks className="w-4 h-4" /> Item-wise Review Status
              </h3>
              <span data-testid="checklist-progress" className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                {checkedCount} / {data.order.lines.length} Prepared
              </span>
            </div>
            <div className="space-y-1.5">
              {data.order.lines.map((ln, idx) => {
                const checked = !!(data.task.checklist || {})[String(idx)];
                const busy = togglingIdx === idx;
                const qty = Number(ln.quantity || 0);
                const unitPrice = Number(ln.unit_price != null ? ln.unit_price : (qty ? (Number(ln.price || 0) / qty) : 0));
                const lineTotal = Number(ln.price != null ? ln.price : unitPrice * qty);
                return (
                  <button
                    key={idx}
                    onClick={() => toggleChecklistItem(idx, !checked)}
                    disabled={busy || isLocked}
                    data-testid={`checklist-item-${idx}`}
                    data-checked={checked ? "true" : "false"}
                    className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition active:scale-[0.99] disabled:opacity-70 ${
                      checked
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-white border-slate-200 hover:border-emerald-300"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-md grid place-items-center shrink-0 mt-0.5 border-2 ${
                      checked ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-transparent"
                    }`}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" /> : checked ? <CheckCircle2 className="w-4 h-4" strokeWidth={2.6} /> : <Circle className="w-3 h-3" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`font-semibold text-sm ${checked ? "line-through text-slate-500" : "text-slate-900"}`}>
                        {ln.product}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {qty} {ln.unit}{unitPrice ? ` · ₹${unitPrice.toFixed(2)} × ${qty} = ₹${lineTotal.toFixed(2)}` : ""}
                      </div>
                    </div>
                    <span data-testid={`item-status-${idx}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shrink-0 ${
                      checked ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}>
                      {checked ? "Prepared" : "Pending Review"}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded-xl p-2.5">
              Tap each item to mark it Prepared. The task auto-completes only once every item is Prepared.
            </div>
          </div>
        )}

        {!isCompleted && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider">Update status</h3>
            <div className="grid grid-cols-3 gap-2">
              {["todo", "in_progress", "completed"].map((s) => {
                const cfg = STATUS_BTN[s];
                const active = t.status === s;
                const blockedByChecklist = s === "completed" && !allItemsPrepared;
                return (
                  <Button key={s} disabled={updating || isCancelled || blockedByChecklist} data-testid={`emp-status-${s}`}
                    onClick={() => updateStatus(s)}
                    className={`h-12 rounded-xl text-xs font-semibold gap-1 ${active ? `${cfg.tone} text-white` : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>
                    <cfg.Icon className={`w-4 h-4 ${s === "in_progress" && active ? "animate-spin" : ""}`} />
                    {cfg.label}
                  </Button>
                );
              })}
            </div>
            {isCancelled && (
              <p className="text-[11px] text-red-700 font-semibold">Cancelled tasks cannot be reopened.</p>
            )}
            {!isCancelled && hasChecklist && !allItemsPrepared && (
              <p className="text-[11px] text-amber-700 font-semibold" data-testid="emp-checklist-gate-hint">
                Mark every item above as Prepared before completing the task.
              </p>
            )}
          </div>
        )}

        {!isLocked && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider">Proof of completion</h3>
            <div>
              <Textarea data-testid="emp-completion-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                placeholder="Notes for your boss (e.g., delivered to gate, paid in cash)" className="rounded-xl text-base" />
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="emp-photo-input" />
            {photo ? (
              <div className="relative">
                <img src={photo} alt="Proof" className="w-full rounded-xl border border-slate-200" data-testid="emp-photo-preview" />
                <button onClick={() => setPhoto("")} data-testid="emp-photo-remove"
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" data-testid="emp-photo-btn"
                onClick={() => fileRef.current?.click()}
                className="w-full h-14 rounded-xl border-dashed gap-2">
                <Camera className="w-5 h-5" /> Add photo (optional)
              </Button>
            )}
            <Button onClick={() => updateStatus("completed")} disabled={updating || !allItemsPrepared} data-testid="emp-mark-complete"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2">
              <Save className="w-5 h-5" /> {updating ? "Saving..." : "Mark Completed"}
            </Button>
            {hasChecklist && !allItemsPrepared && (
              <p className="text-[11px] text-amber-700 font-semibold text-center">
                Complete all items in Item-wise Review Status above first.
              </p>
            )}
          </div>
        )}

        {t.status === "completed" && (t.completion_note || t.completion_photo) && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 space-y-3" data-testid="emp-completion-summary">
            <div className="flex items-center gap-2 text-emerald-800 font-bold"><CheckCircle2 className="w-5 h-5" /> Completed</div>
            {t.completion_note && <p className="text-sm text-emerald-900 whitespace-pre-wrap">{t.completion_note}</p>}
            {t.completion_photo && <img src={t.completion_photo} alt="Proof" className="w-full rounded-xl border border-emerald-200" />}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="task-note-card">
          <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider flex items-center gap-1.5">
            <MessageSquarePlus className="w-4 h-4" /> Add Note
          </h3>
          <p className="text-xs text-slate-500">Visible to your business owner/admin for this task.</p>
          <Textarea data-testid="task-note-input" value={taskNote} onChange={(e) => setTaskNote(e.target.value)} rows={3}
            disabled={isLocked} placeholder="e.g., Item out of stock, will deliver tomorrow" className="rounded-xl text-base" />
          <Button onClick={saveTaskNote} disabled={savingNote || isLocked} data-testid="task-note-save"
            className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
            {savingNote ? "Saving..." : "Save Note"}
          </Button>
        </div>
      </div>
    </>
  );
}

function ContactRow({ icon: Icon, tone, label, name, phone, testid }) {
  const tones = {
    indigo: "bg-indigo-100 text-indigo-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div data-testid={testid} className="flex items-center gap-3 pt-2 border-t border-slate-100">
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="font-semibold text-slate-900 truncate">{name}</div>
      </div>
      {phone && (
        <a href={`tel:${phone}`} data-testid={`${testid}-call`}
          className="h-11 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center gap-1 shrink-0">
          <Phone className="w-4 h-4" /> Call
        </a>
      )}
    </div>
  );
}
